import { DomainEntity, Id, Json } from '@homeleite/arch';
import { EMPTY, normalizeUpperCase } from '@homeleite/common-utils';
import { AnimalVariety } from './animal-variety';
import { BreederSummary } from '../breeders/breeder';
import { BreedSummary } from '../breeds/breed';
import { ProducerSummary } from '../producers/producer';
import { PropertySummary } from '../properties/property';
import { Gender } from './gender';
import { AuditData } from './auditData';

export class AnimalSummary implements DomainEntity {
    constructor(
        public readonly id = null,
        public apcId: string = EMPTY,
        public name: string = EMPTY,
        public register: string = EMPTY,
        public nickname: string = EMPTY,
        public birthdate: Date = null,
        public imageUrl: string = null,
        public imageId: string = null,
        public mother: AnimalSummary = null,
        public numberOfChildren = 0,
        public isActive = true
    ) {
        this.nickname = normalizeUpperCase(this.nickname);
    }

    toJson(): any {
        return Json.to.simple(this);
    }

    static fromJson(json: any): AnimalSummary {
        if (!json) {
            return null;
        }
        const situation = json.situation;
        const isActive =
            situation === 'B' || situation === 'b'
                ? false
                : json.isActive ?? json.active ?? true;
        return Json.from.simple(json, AnimalSummary, {
            nickname: normalizeUpperCase(json.nickname),
            isActive
        });
    }

    static createBy(animal: Animal): AnimalSummary {
        return new AnimalSummary(
            animal.id,
            animal.apcId,
            animal.name,
            animal.register,
            animal.nickname,
            animal.birthdate,
            animal.imageUrl,
            animal.imageId,
            animal.mother,
            animal.numberOfChildren,
            animal.isActive
        );
    }
}

export class Animal implements DomainEntity {
    constructor(
        public readonly id = Id.gen(),
        public apcId: string = null,
        public name: string = EMPTY,
        public register: string = EMPTY,
        public nickname: string = EMPTY,
        public blood: string = EMPTY,
        public birthdate: Date = null,
        public diedAt: Date = null,
        public producer: ProducerSummary = null,
        public property: PropertySummary = new PropertySummary(),
        public imageUrl: string = null,
        public imageId: string = null,
        public gender: Gender = null,
        public breed: BreedSummary = new BreedSummary(),
        public isFiv = false,
        public isTe = false,
        public mother: AnimalSummary = null,
        public motherName: string = EMPTY,
        public numberOfChildren = 0,
        public motherRegister: string = EMPTY,
        public father: BreederSummary = new BreederSummary(),
        public fatherRegister: string = EMPTY,
        public fatherName: string = EMPTY,
        public official = false,
        public lastControlDate: Date = null,
        public totalMilkProduction = 0,
        public flaskNumber = 0,
        public variety: AnimalVariety = null,
        public lastEvent: string = EMPTY,
        public electronicBottom: string = null,
        public classification: string = EMPTY,
        public proteinPercentage = 0,
        public somaticCells = 0,
        public ureaPercentage = 0,
        public fatPercentage = 0,
        public lactationDays = 0,
        public belongsToProducer = false,
        public mustAppearOnMilkControl = false,
        public modifiedDate: string = new Date().toISOString(),
        public isActive = true,
        public auditData: AuditData = null,
        public situation: string = null,
    ) {
        this.nickname = normalizeUpperCase(this.nickname);
    }

    toJson(): any {
        return Json.to.simple(this, {
            birthdate: this.birthdate?.toISOString() ?? null,
            diedAt: this.diedAt?.toISOString?.() ?? null,
            lastControlDate: this.lastControlDate?.toISOString?.() ?? null,
            producer: this.producer?.toJson() ?? null,
            property: this.property?.toJson() ?? null,
            motherName: this.motherName ?? null,
            gender: this.gender?.name ?? null,
            breed: this.breed?.toJson() ?? null,
            mother: this.mother?.toJson() ?? null,
            father: this.father?.toJson() ?? null,
            variety: this.variety?.description ?? null,
            auditData: this.auditData?.toJson() ?? null,
            situation: this.situation ?? null
        });
    }

    static fromJson(json: any): Animal {
        if (!json) {
            return null;
        }
        // Mongo/ep-arch grava `active`; API DTO usa `isActive`. Situação B = fora do rebanho.
        const situation = json.situation ?? null;
        const isActive =
            situation === 'B' || situation === 'b'
                ? false
                : json.isActive ?? json.active ?? true;
        return Json.from.simple(json, Animal, {
            nickname: normalizeUpperCase(json.nickname),
            birthdate: json.birthdate ? new Date(json.birthdate) : null,
            diedAt: json.diedAt ? new Date(json.diedAt) : null,
            producer: json.producer ? ProducerSummary.fromJson(json.producer) : null,
            property: json.property ? PropertySummary.fromJson(json.property) : null,
            gender: json.gender ? Gender.get(json.gender.name || json.gender) : null,
            breed: json.breed ? BreedSummary.fromJson(json.breed) : null,
            mother: json.mother ? AnimalSummary.fromJson(json.mother) : null,
            motherName: json.motherName ?? EMPTY,
            motherRegister: json.motherRegister ?? json.mother?.register ?? EMPTY,
            father: json.father ? BreederSummary.fromJson(json.father) : null,
            fatherName: json.fatherName ?? EMPTY,
            fatherRegister: json.fatherRegister ?? json.father?.register ?? EMPTY,
            lastControlDate: json.lastControlDate ? new Date(json.lastControlDate) : null,
            variety: json.variety ? AnimalVariety.get(json.variety.description || json.variety) : null,
            auditData: json.auditData ? AuditData.fromJson(json.auditData) : null,
            situation,
            isActive
        });
    }

    static createBy(
        animal: Animal,
        id: string,
        producerSummary: ProducerSummary
    ) {
        return new Animal(
            id,
            animal.apcId,
            animal.name,
            animal.register,
            animal.nickname,
            animal.blood,
            animal.birthdate,
            animal.diedAt,
            producerSummary,
            animal.property,
            animal.imageUrl,
            animal.imageId,
            animal.gender,
            animal.breed,
            animal.isFiv,
            animal.isTe,
            animal.mother,
            animal.motherName,
            animal.numberOfChildren,
            animal.motherRegister,
            animal.father,
            animal.fatherRegister,
            animal.fatherName,
            animal.official,
            animal.lastControlDate,
            animal.totalMilkProduction,
            animal.flaskNumber,
            animal.variety,
            animal.lastEvent,
            animal.electronicBottom,
            animal.classification,
            animal.proteinPercentage,
            animal.somaticCells,
            animal.ureaPercentage,
            animal.fatPercentage,
            animal.lactationDays,
            animal.belongsToProducer,
            animal.mustAppearOnMilkControl,
            animal.modifiedDate,
            animal.isActive,
            animal.auditData
        );
    }
}
